export type ViewerEnvironmentSourceType = 'none' | 'hdri' | 'preset';

export interface ViewerEnvironmentEntry {
  id: string;
  label: string;
  sourceType: ViewerEnvironmentSourceType;
  source?: string;
  assetId?: string | null;
}

export interface ViewerEnvironmentSettings {
  entries: ViewerEnvironmentEntry[];
}

export const VIEWER_ENVIRONMENTS_KEY = 'viewer_environments';

export const DEFAULT_VIEWER_ENVIRONMENT_SETTINGS: ViewerEnvironmentSettings = {
  entries: [
    { id: 'none', label: 'None (Softbox Only)', sourceType: 'none' },
    { id: 'apartment', label: 'Apartment', sourceType: 'preset', source: 'apartment' },
    { id: 'studio', label: 'Studio', sourceType: 'hdri', source: '/hdri/studio_small_03_1k.hdr' },
    { id: 'city', label: 'City', sourceType: 'hdri', source: '/hdri/potsdamer_platz_1k.hdr' },
    { id: 'sunset', label: 'Sunset', sourceType: 'preset', source: 'sunset' },
  ],
};

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);

export const createViewerEnvironmentId = (label: string, existingIds: string[]) => {
  const base = slugify(label) || 'environment';
  let next = base;
  let suffix = 2;
  while (existingIds.includes(next)) {
    next = `${base}-${suffix}`;
    suffix += 1;
  }
  return next;
};

export const normalizeViewerEnvironmentSettings = (value: unknown): ViewerEnvironmentSettings => {
  const raw =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const entriesRaw = Array.isArray(raw.entries) ? raw.entries : [];
  const normalized: ViewerEnvironmentEntry[] = [];
  const seenIds = new Set<string>();

  entriesRaw.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return;
    const candidate = entry as Record<string, unknown>;
    const sourceType =
      candidate.sourceType === 'none' || candidate.sourceType === 'hdri' || candidate.sourceType === 'preset'
        ? candidate.sourceType
        : null;
    if (!sourceType) return;

    const rawLabel = typeof candidate.label === 'string' ? candidate.label.trim() : '';
    const label = rawLabel || `Environment ${index + 1}`;
    const requestedId = typeof candidate.id === 'string' ? candidate.id.trim() : '';
    const idBase = requestedId || slugify(label) || `environment-${index + 1}`;
    let id = idBase;
    let dedupe = 2;
    while (seenIds.has(id)) {
      id = `${idBase}-${dedupe}`;
      dedupe += 1;
    }
    seenIds.add(id);

    const source = typeof candidate.source === 'string' ? candidate.source.trim() : '';
    const assetId = typeof candidate.assetId === 'string' && candidate.assetId.trim().length > 0
      ? candidate.assetId.trim()
      : null;

    if (sourceType === 'none') {
      normalized.push({ id, label, sourceType: 'none', assetId: null });
      return;
    }

    if (!source) return;
    normalized.push({ id, label, sourceType, source, assetId });
  });

  const hasNone = normalized.some((entry) => entry.id === 'none' && entry.sourceType === 'none');
  if (!hasNone) {
    normalized.unshift(clone(DEFAULT_VIEWER_ENVIRONMENT_SETTINGS.entries[0]));
  }

  if (normalized.length === 0) {
    return clone(DEFAULT_VIEWER_ENVIRONMENT_SETTINGS);
  }

  return { entries: normalized };
};

export const environmentOptionsFromRegistry = (settings: ViewerEnvironmentSettings) =>
  settings.entries
    .filter((entry) => entry.sourceType === 'none' || Boolean(entry.source))
    .map((entry) => ({
      value: entry.id,
      label: entry.label,
    }));

export const getViewerEnvironmentEntry = (
  settings: ViewerEnvironmentSettings,
  id?: string | null
) => settings.entries.find((entry) => entry.id === id) ?? null;

export const getViewerEnvironmentProps = (
  settings: ViewerEnvironmentSettings,
  id?: string | null
) => {
  const entry = getViewerEnvironmentEntry(settings, id);
  if (!entry || entry.sourceType === 'none') return null;
  if (entry.sourceType === 'hdri') {
    return { files: entry.source as string } as const;
  }
  return { preset: entry.source as any } as const;
};
